import { useQuery } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import { useMemo, useState } from "react";
import { useGraphql } from "~/api-client";
import type {
  AutoCompleteAddressDetails,
  AutocompleteAddress,
  DeliveryAddress,
} from "~/types";

export function useAddresses(sessionToken: string) {
  const [queryValue, setQueryValue] = useState("");
  const [locationRef, setLocationRef] = useState<string>();

  const { getAddresses } = useGraphql();
  const {
    data,
    error,
    isFetching: loading,
  } = useQuery({
    queryKey: ["addresses", queryValue, locationRef],
    queryFn: () => {
      if (!queryValue) return null;
      return getAddresses(queryValue, sessionToken, locationRef);
    },
    refetchOnWindowFocus: false,
  });

  const queryAddresses = useMemo(() => {
    const load = (query: string, locationRef?: string) => {
      setQueryValue(query);
      setLocationRef(locationRef);
    };
    return debounce(
      (query: string, locationRef?: string) => load(query, locationRef),
      300,
    );
  }, []);

  const addresses: AutocompleteAddress[] = data?.addressAutocomplete || [];
  return { error, addresses, queryAddresses, loading };
}

export function useGetAddress(sessionToken: string) {
  const graphql = useGraphql();

  return async function getAddress(
    ref: string,
  ): Promise<{ data: { addressDetails: AutoCompleteAddressDetails } }> {
    const data = await graphql.getAddressDetails(ref, sessionToken);
    const addressDetails = data.addressDetails.details;
    if (!addressDetails) {
      throw new Error(
        data.addressDetails.error?.message ?? "An error occurred",
      );
    }
    return {
      data: {
        addressDetails,
      },
    };
  };
}

export function useDeliveryAddressCheck(
  locationRef: string,
  address: DeliveryAddress | null,
) {
  const { checkDeliveryAddress } = useGraphql();
  const {
    error,
    isFetching: loading,
    data,
  } = useQuery({
    queryKey: ["check-delivery-route", locationRef, address],
    queryFn: () =>
      address ? checkDeliveryAddress(locationRef, address) : null,
    refetchOnWindowFocus: true,
  });

  const valid = Boolean(data?.ordering?.deliveryAddressStatus?.valid);
  const messages = data?.ordering?.deliveryAddressStatus?.messages || [];

  return { loading, error, valid, messages };
}

export function useFindPlace(query: string) {
  const { getFindPlace } = useGraphql();
  const {
    data,
    error,
    isFetching: loading,
  } = useQuery({
    queryKey: ["find-place", query],
    queryFn: () => getFindPlace(query),
    enabled: query !== "",
    refetchOnWindowFocus: false,
  });

  const place = data?.findPlace ?? null;
  return { place, error, loading };
}
